import React, { createContext, useCallback, useContext, useMemo } from 'react';
import type { RouteSearchData } from '@cko/dashboard-shared';
import {
  BreadCrumb,
  FlattenedRoute,
  FlattenedRoutes,
  MenuItem,
  PageDefinition,
  ViewDefinition,
} from '../../config/shared/types';
import { resolveExternalLinkUrl } from '../../config/shared/library/utils/resolveExternalLinkUrl';
import { useUiConfig } from '../UiConfigProvider';
import { useUserContextValidation } from '../UserContextValidationProvider';
import { Route, RouteRedirect } from '../../config/shared/types/route';
import {
  AvailableExternalLinkMenuItem,
  AvailableGroupMenuItem,
  AvailableInternalLinkMenuItem,
  AvailableMenuItem,
  Navigation,
} from './types';

export const NavigationProvider = ({ children }: { children: React.ReactNode }) => {
  const { routes, pages, menus, views } = useUiConfig();
  const { validate } = useUserContextValidation();

  const getFlattenedRoutes = useCallback(
    (
      _routes: Array<Route>,
      parentPath = '',
      parentBreadcrumbs: BreadCrumb[] = [],
    ): FlattenedRoutes => {
      const flatRoutes: FlattenedRoutes = {};

      _routes.forEach((route) => {
        const {
          id,
          path,
          child_routes: childRoutes,
          title,
          not_searchable: notSearchable,
          ...rest
        } = route;
        const currentPath = `${parentPath}/${path}`.replace(/\/+/g, '/');

        const newBreadcrumbs = title
          ? [...parentBreadcrumbs, { title: title, routeId: id }]
          : parentBreadcrumbs;

        flatRoutes[id] = {
          path: currentPath,
          breadcrumbs: newBreadcrumbs,
          title,
          notSearchable,
          ...rest,
        };

        /*
        At this point we dont know which child route is available to the user as we haven't filtered by permissions yet.
        We indicate this route is a redirect and what potential child routes to send to, we then decide when rendering 
        the routes post filtering which if any we should send the user to. 
      */
        if (!flatRoutes[id]?.page && childRoutes && childRoutes.length > 0) {
          flatRoutes[id].shouldRedirect = true;
          flatRoutes[id].redirectRouteIds = childRoutes.map((childRoute: Route) => childRoute.id);
        }

        /**
         * This redirect functionality is a WIP and should not be used by anyone outside of the CORE team
         * until the final implementation has been decided.
         */
        const hasValidRedirect =
          flatRoutes[id]?.redirect &&
          validate({ feature_flags: flatRoutes[id].redirect?.feature_flag }, 'REDIRECT');
        if (hasValidRedirect) {
          flatRoutes[id].shouldRedirect = true;
          flatRoutes[id].redirectRouteIds = (flatRoutes[id].redirect as RouteRedirect).route_ids;
        }

        if (childRoutes) {
          Object.assign(flatRoutes, getFlattenedRoutes(childRoutes, currentPath, newBreadcrumbs));
        }
      });
      return flatRoutes;
    },
    [validate],
  );

  const flattenedRoutes: FlattenedRoutes = useMemo(() => getFlattenedRoutes(routes), [routes]);

  const validateRoutes = useCallback((unvalidatedRoutes: FlattenedRoutes): FlattenedRoutes => {
    return Object.keys(unvalidatedRoutes).reduce<FlattenedRoutes>(
      (prev: FlattenedRoutes, current: string) => {
        const routeIsValid = validate(unvalidatedRoutes[current], 'ROUTE');
        if (routeIsValid) {
          prev[current] = unvalidatedRoutes[current];
        }

        return prev;
      },
      {},
    );
  }, []);

  const availableRoutes = useMemo(
    () => validateRoutes(flattenedRoutes),
    [flattenedRoutes, validate],
  );

  const getRouteById = useCallback(
    (routeId: string): FlattenedRoute | undefined => {
      return availableRoutes[routeId] ?? undefined;
    },
    [availableRoutes],
  );

  const filterMenuItemsByAvailableRoutes = useCallback(
    (menuItems: Array<MenuItem>): Array<AvailableMenuItem> => {
      return menuItems
        .map((menuItem) => {
          if (menuItem.type === 'external_link') {
            const availableExternalLinkMenuItem: AvailableExternalLinkMenuItem = {
              label: menuItem.label,
              iconName: menuItem?.icon_name,
              statusLabel: menuItem?.status_label,
              type: 'external_link',
              url: resolveExternalLinkUrl(menuItem.url),
            };

            return availableExternalLinkMenuItem;
          }

          if (menuItem.type === 'internal_link') {
            const route = availableRoutes[menuItem.route_id];
            if (!route) {
              return null;
            }

            const availableInternalLinkMenuItem: AvailableInternalLinkMenuItem = {
              label: menuItem.label,
              iconName: menuItem?.icon_name,
              path: route.path,
              requiredAccountLevels: route.required_account_levels,
              routeId: menuItem.route_id,
              statusLabel: menuItem?.status_label,
              type: 'internal_link',
              description: availableRoutes[menuItem.route_id]?.description,
            };

            return availableInternalLinkMenuItem;
          }

          const availableGroupMenuItem: AvailableGroupMenuItem = {
            availableChildItems: filterMenuItemsByAvailableRoutes(menuItem.child_menu_items),
            iconName: menuItem?.icon_name,
            label: menuItem.label,
            statusLabel: menuItem?.status_label,
            type: 'group',
          };

          return availableGroupMenuItem;
        })
        .filter((menuItem) => {
          if (!menuItem) {
            return false;
          }

          if (menuItem.type === 'group') {
            return menuItem.availableChildItems.length > 0;
          }

          return true;
        }) as Array<AvailableMenuItem>;
    },
    [availableRoutes],
  );

  const filteredMenuItems: Record<string, Array<AvailableMenuItem>> = useMemo(() => {
    const filteredMenus: Record<string, Array<AvailableMenuItem>> = {};
    Object.keys(menus).forEach((key) => {
      filteredMenus[key] = filterMenuItemsByAvailableRoutes(menus[key]);
    });
    return filteredMenus;
  }, [filterMenuItemsByAvailableRoutes, menus]);

  const getPageById = useCallback(
    (pageId: string): PageDefinition | undefined => {
      return pages[pageId];
    },
    [pages],
  );

  const getViewById = useCallback(
    (viewId: string): ViewDefinition | undefined => {
      return views[viewId];
    },
    [views],
  );

  // see if we can use path match lib to do the replacing
  const getUrlForRoute = useCallback(
    (routeId: string, params?: Record<string, string>): string | undefined => {
      const route = availableRoutes[routeId];
      if (!route) {
        return undefined;
      }

      let url = route.path;
      if (params) {
        Object.entries(params).forEach(([key, value]) => {
          url = url.replace(`:${key}`, value);
        });
      }

      return url;
    },
    [availableRoutes],
  );

  const getSearchDataByParentRouteId = useCallback(
    (parentRouteId: string) => {
      const parentRoute = routes.find((route) => route.id === parentRouteId);

      if (!parentRoute) {
        return [];
      }

      const flattenedRouteSubsection = getFlattenedRoutes([parentRoute]);
      const availableRouteSubsection = validateRoutes(flattenedRouteSubsection);

      const searchData: RouteSearchData[] = [];

      for (const key in availableRouteSubsection) {
        const route = availableRouteSubsection[key];
        if (!route?.notSearchable) {
          const { title, keywords, breadcrumbs, description } = route;
          searchData.push({
            routeId: key,
            routeUrl: getUrlForRoute(key),
            title,
            keywords,
            breadcrumbs,
            description,
          });
        }
      }
      return searchData;
    },
    [getFlattenedRoutes, getUrlForRoute, routes, validateRoutes],
  );

  const contextValues = useMemo(
    () => ({
      availableRoutes,
      filteredMenuItems,
      getPageById,
      getRouteById,
      getViewById,
      getUrlForRoute,
      getSearchDataByParentRouteId,
    }),
    [
      availableRoutes,
      filteredMenuItems,
      getPageById,
      getRouteById,
      getViewById,
      getUrlForRoute,
      getSearchDataByParentRouteId,
    ],
  );

  return (
    <NavigationCoreContext.Provider value={contextValues}>
      {children}
    </NavigationCoreContext.Provider>
  );
};

export const NavigationCoreContext = createContext<Navigation | null>(null);

export function useNavigation() {
  const context = useContext(NavigationCoreContext);

  if (context === null) {
    throw new Error('useNavigation must be used within a NavigationProvider');
  }

  return context;
}
